import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import { NgxFileDropEntry } from 'ngx-file-drop';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subject,
    catchError,
    firstValueFrom,
    map,
    defer,
    from,
    lastValueFrom,
    mergeMap,
    of,
    switchMap,
    tap,
    toArray,
} from 'rxjs';
import {
    FREE_PLAN_IMAGE_LIMIT,
    ImgDbUploadUtilService,
} from './img-db-upload-util.service';
import { ImgUpload, UploadValidationError } from '../model/img-upload';
import { ToastMessageService } from 'src/app/shared/services/toast-message.service';
import {
    ImageDbUploadTarget,
    UploadErrorSummaryPayload,
    UploadJob,
    UploadStatus,
    UploadTargetInfo,
} from 'src/app/shared/models/upload-job';
import { ImgDbUploadErrorStoreService } from './img-db-upload-error-store.service';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { SubscriptionType } from 'src/app/shared/models/credit-service-dtos';

@Injectable({
    providedIn: 'root',
})
export class ImgDbUploadService {
    batchSize: number = 100;
    maxConcurrentUpload = 3;
    interval = null;
    processingPollInterval = null;
    queue: Array<{ job: UploadJob; files: File[] }> = [];
    uploadProcessState: EventEmitter<void> = new EventEmitter<void>();

    uploadJobs$ = new BehaviorSubject<UploadJob[]>([]);
    uploadCompleted$ = new Subject<string>();

    private newInQueue: EventEmitter<void> = new EventEmitter<void>();
    private workerFinished: EventEmitter<void> = new EventEmitter<void>();
    private workerRunning: boolean;
    private viewedImgDbId: string = '';
    private activeEntry: { job: UploadJob; files: File[] } | null = null;
    private suppressCompletionFeedbackForJobIds = new Set<string>();
    private hasActiveUploads$ = new Subject<boolean>();
    private beforeUnloadHandler = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = '';
    };

    constructor(
        private http: HttpClient,
        private imgDbUploadUtilService: ImgDbUploadUtilService,
        private toastMessageService: ToastMessageService,
        private uploadErrorStoreService: ImgDbUploadErrorStoreService,
        private authenticationService: AuthenticationService
    ) {
        this.workerRunning = false;
        this.newInQueue.subscribe(() => this.processEvent());
        this.workerFinished.subscribe(() => this.orchestrateWorker());
        this.processingPollInterval = setInterval(() => {
            this.pollProcessingJobs();
        }, 5000);
        this.hasActiveUploads$.subscribe((hasActiveUploads) => {
            if (hasActiveUploads) {
                window.addEventListener(
                    'beforeunload',
                    this.beforeUnloadHandler
                );
            } else {
                window.removeEventListener(
                    'beforeunload',
                    this.beforeUnloadHandler
                );
            }
        });
    }

    setViewedImgDbId(imgDbId: string) {
        this.viewedImgDbId = imgDbId;
    }

    addToUploadQueue(
        imgDbId: string,
        filesParam: NgxFileDropEntry[],
        targetInfo?: UploadTargetInfo
    ) {
        this.shouldBlockFreePlanSelection(filesParam.length).then(
            (shouldBlock) => {
                if (shouldBlock) {
                    this.imgDbUploadUtilService.showFreePlanUploadSelectionLimitExceededError();
                    this.uploadProcessState.emit();
                    return;
                }

                this.imgDbUploadUtilService
                    .validateFiles(filesParam)
                    .then(({ validFiles, errors }) => {
                        const invalidFormatCount = this.countValidationErrors(
                            errors,
                            'invalidFormat'
                        );
                        const fileTooLargeCount = this.countValidationErrors(
                            errors,
                            'fileTooLarge'
                        );
                        const totalCount =
                            (validFiles?.length ?? 0) +
                            invalidFormatCount +
                            fileTooLargeCount;
                        if (totalCount === 0) {
                            // Currently workaround for remaining spinner in gallery
                            this.uploadProcessState.emit();
                            return;
                        }

                        const job = this.createUploadJob(
                            imgDbId,
                            targetInfo,
                            totalCount,
                            invalidFormatCount,
                            fileTooLargeCount
                        );
                        if (validFiles?.length > 0) {
                            this.queue.push({ job, files: validFiles });
                            this.newInQueue.emit();
                        } else {
                            // All files failed validation - complete immediately and emit state
                            this.completeJob(job, false, true).subscribe(() => {
                                this.uploadProcessState.emit();
                            });
                        }
                    });
            }
        );
    }

    private async shouldBlockFreePlanSelection(
        selectedFileCount: number
    ): Promise<boolean> {
        if (selectedFileCount <= FREE_PLAN_IMAGE_LIMIT) {
            return false;
        }
        const userId = this.authenticationService.currentUserValue?.id;
        if (!userId) {
            return false;
        }
        return firstValueFrom(
            this.authenticationService.getCurrentUserSubscription().pipe(
                map(
                    (subscription) =>
                        subscription?.subscriptionType === SubscriptionType.FREE
                ),
                catchError(() => of(false))
            )
        );
    }

    /**
     * Triggered when a new event/entry is added to queue
     */
    private processEvent(): void {
        if (!this.workerRunning) {
            // not running, start worker
            this.workerRunning = true;
            this.hasActiveUploads$.next(true);
            this.orchestrateWorker();
        }
    }

    private orchestrateWorker(): void {
        if (this.queue.length > 0) {
            let entry = this.queue.shift();
            this.activeEntry = entry;
            this.processInBatches(entry);
        } else {
            this.workerRunning = false;
            this.activeEntry = null;
            this.cancelRefreshImages();
            // Emit final refresh to show newly uploaded images
            this.uploadProcessState.emit();
            this.hasActiveUploads$.next(false);
        }
    }

    /**
     * Process in small batches based on batchSize.
     * Is also the "Worker", which controls when it is done
     * @param entry QueueEntry
     */
    private async processInBatches(entry: { job: UploadJob; files: File[] }) {
        this.updateJobStatus(entry.job, 'uploading');
        let start = 0,
            end = this.batchSize;
        while (start < entry.files.length) {
            if (entry.job.cancelRequested) {
                break;
            }
            let batchFiles: File[] = entry.files.slice(start, end);
            await this.processing(entry.job, batchFiles);
            start = end;
            end += this.batchSize;
        }

        if (entry.job.cancelRequested || entry.job.successCount === 0) {
            await lastValueFrom(
                this.completeJob(entry.job, entry.job.cancelRequested, true)
            );
        } else {
            this.updateJobStatus(entry.job, 'processing');
        }
        this.workerFinished.emit();
    }

    private processing(job: UploadJob, files: File[]): Promise<any[]> {
        let obsBatch$ = this.imgDbUploadUtilService
            .generateSignedUrls(files, job.imageDbId, job.uploadSessionId)
            .pipe(
                switchMap((data) => {
                    if (data.length === 0 && files.length > 0) {
                        this.suppressCompletionFeedbackForJobIds.add(job.id);
                        return of([]);
                    }
                    return this.uploadParallel(job, data);
                })
            );
        return lastValueFrom(obsBatch$);
    }

    private uploadParallel(
        job: UploadJob,
        files: ImgUpload[]
    ): Observable<any[]> {
        let parallelUploads$ = from(files).pipe(
            mergeMap(
                (image: ImgUpload) =>
                    // Use defer to check cancelRequested at the moment each upload starts,
                    // not when the observable is created (all files pass through synchronously)
                    defer(() => {
                        if (job.cancelRequested) {
                            return EMPTY;
                        }
                        return this.uploadFile(image.url, image.file).pipe(
                            tap(() => {
                                this.markUploadSuccess(job);
                            }),
                            catchError((err, _caught) => {
                                this.markUploadFailure(job, err);
                                return of();
                            })
                        );
                    }),
                this.maxConcurrentUpload
            ),
            toArray()
        );

        this.refreshImages(job.imageDbId);
        return parallelUploads$;
    }

    private uploadFile(url: string, file: File): Observable<any> {
        // CORS problem, if sent is aborted see google bucket CORS config
        return this.http.put(url, file, {
            headers: { 'Content-Type': 'image/jpeg' },
        });
    }

    requestCancel(jobId: string) {
        if (this.activeEntry?.job.id === jobId) {
            this.activeEntry.job.cancelRequested = true;
            this.updateJob(this.activeEntry.job);
            return;
        }
        const index = this.queue.findIndex((entry) => entry.job.id === jobId);
        if (index >= 0) {
            const [entry] = this.queue.splice(index, 1);
            this.removeJob(entry.job.id);
        }
    }

    private markUploadSuccess(job: UploadJob) {
        job.successCount += 1;
        job.processedCount += 1;
        this.updateJob(job);
    }

    private markUploadFailure(job: UploadJob, err: unknown): void {
        if (err instanceof HttpErrorResponse) {
            console.error(err);
        }
        job.uploadFailedCount += 1;
        job.processedCount += 1;
        this.updateJob(job);
    }

    private updateJobStatus(job: UploadJob, status: UploadStatus) {
        job.status = status;
        this.updateJob(job);
    }

    private updateJob(job: UploadJob) {
        const jobs = this.uploadJobs$.value;
        const index = jobs.findIndex((item) => item.id === job.id);
        if (index >= 0) {
            const updated = [...jobs];
            updated[index] = { ...job };
            this.uploadJobs$.next(updated);
            return;
        }
        this.uploadJobs$.next([...jobs, { ...job }]);
    }

    private removeJob(jobId: string) {
        this.uploadJobs$.next(
            this.uploadJobs$.value.filter((job) => job.id !== jobId)
        );
    }

    private createUploadJob(
        imageDbId: string,
        targetInfo: UploadTargetInfo | undefined,
        totalCount: number,
        invalidFormatCount: number,
        fileTooLargeCount: number
    ): UploadJob {
        const job: UploadJob = {
            id: this.generateJobId(),
            uploadSessionId: this.generateUploadSessionId(),
            imageDbId,
            target: this.ensureTargetInfo(imageDbId, targetInfo),
            status: 'queued',
            totalCount,
            processedCount: invalidFormatCount + fileTooLargeCount,
            successCount: 0,
            invalidFormatCount,
            fileTooLargeCount,
            uploadFailedCount: 0,
            cancelRequested: false,
        };
        this.updateJob(job);
        return job;
    }

    private completeJob(
        job: UploadJob,
        wasCancelled: boolean,
        showCompletionToast: boolean
    ): Observable<void> {
        if (this.suppressCompletionFeedbackForJobIds.delete(job.id)) {
            this.removeJob(job.id);
            this.uploadCompleted$.next(job.imageDbId);
            return of(void 0);
        }

        const { level, message } =
            this.toastMessageService.getUploadCompletionToast(job);
        const hasErrors = level === 'warning';

        if (wasCancelled) {
            this.updateJobStatus(job, 'cancelled');
            // Persist and show failed upload info even when cancelled
            if (hasErrors) {
                if (showCompletionToast) {
                    this.showCompletionToast(level, message);
                }
                return this.persistUploadErrors(job).pipe(
                    tap(() => {
                        this.removeJob(job.id);
                        this.uploadCompleted$.next(job.imageDbId);
                    })
                );
            }
            this.removeJob(job.id);
            this.uploadCompleted$.next(job.imageDbId);
            return of(void 0);
        }

        this.updateJobStatus(job, 'completed');
        if (showCompletionToast) {
            this.showCompletionToast(level, message);
        }
        if (hasErrors) {
            return this.persistUploadErrors(job).pipe(
                tap(() => {
                    this.removeJob(job.id);
                    this.uploadCompleted$.next(job.imageDbId);
                })
            );
        }
        this.removeJob(job.id);
        this.uploadCompleted$.next(job.imageDbId);
        return of(void 0);
    }

    private showCompletionToast(level: 'success' | 'warning', message: string) {
        if (level === 'success') {
            this.toastMessageService.showPersistentSuccessMsg(message);
        } else {
            this.toastMessageService.showPersistentWarningMsg(message);
        }
    }

    private persistUploadErrors(job: UploadJob): Observable<void> {
        const payload: UploadErrorSummaryPayload = {
            totalCount: job.totalCount,
            invalidFormatCount: job.invalidFormatCount,
            fileTooLargeCount: job.fileTooLargeCount,
            uploadFailedCount: job.uploadFailedCount,
        };
        return this.uploadErrorStoreService
            .saveUploadErrorSummary(job.imageDbId, payload)
            .pipe(
                catchError((err) => {
                    console.error(err);
                    return of(void 0);
                })
            );
    }

    private countValidationErrors(
        errors: UploadValidationError[],
        reason: UploadValidationError['reason']
    ): number {
        return errors.filter((error) => error.reason === reason).length;
    }

    private ensureTargetInfo(
        imageDbId: string,
        targetInfo?: UploadTargetInfo
    ): UploadTargetInfo {
        if (targetInfo) {
            return {
                ...targetInfo,
                imageDbId,
            };
        }
        return {
            type: 'imageDb',
            imageDbId,
            imageDbName: imageDbId,
        } as ImageDbUploadTarget;
    }

    private generateJobId(): string {
        return crypto.randomUUID();
    }

    private generateUploadSessionId(): string {
        return crypto.randomUUID();
    }

    private async pollProcessingJobs(): Promise<void> {
        const processingJobs = this.uploadJobs$.value.filter(
            (job) => job.status === 'processing'
        );
        for (const job of processingJobs) {
            const active = await firstValueFrom(
                this.imgDbUploadUtilService.hasActiveUploadProcessingSession(
                    job.uploadSessionId
                )
            ).catch(() => true);

            const currentJob = this.uploadJobs$.value.find(
                (item) => item.id === job.id
            );
            if (currentJob?.status !== 'processing') {
                continue;
            }

            if (!active) {
                await lastValueFrom(this.completeJob(currentJob, false, true));
            }

            if (this.viewedImgDbId === job.imageDbId) {
                this.uploadProcessState.emit();
            }
        }
    }

    private refreshImages(imgDbId: string) {
        if (!this.interval && this.viewedImgDbId === imgDbId) {
            this.setRefreshInterval();
        }
    }

    private setRefreshInterval() {
        this.interval = setInterval(() => {
            this.uploadProcessState.emit();
        }, 2000);
    }

    cancelRefreshImages() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    }
}
